function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React from 'react';
import produce from 'immer';
import { DraggableBehavior, useBehaviorsList } from 'common/query-builder/query-entry-section/row-types/new-metric-entry/common/behaviors-list';
import { RetentionQuerySettingsReact } from 'common/query-builder/query-entry-section/row-types/metric-entry/metrics/retention/retention-query-settings';
import { getRetentionCountingMethod } from 'common/query-builder/query-entry-section/row-types/metric-entry/metrics/retention/utils';
import { RetentionQuerySettingsUpdate } from 'common/query-builder/query-entry-section/row-types/metric-entry/metrics/retention/types';
import { MetricType } from 'common/types/reports/bookmark';
import { useTranslation } from 'common/i18n/hooks';
import { SelectReact } from 'common/components/select';
import { SelectTheme } from 'common/components/select/constants';
import { ButtonSize } from 'common/components/button/constants';
import { DisplaySize } from 'common/components/tooltip/types';
import { ChangeEvent } from 'common/components/select/types';
import { SectionItem } from 'common/components/items-menu/types';
import type { RowProps } from '../catalog';
import styles from './index.module.styl';
import { RetentionCriteria } from './retention-criteria';
export function RetentionBehaviorDefinition(props: RowProps) {
  const {
    t
  } = useTranslation();
  const {
    behaviors,
    handleUpdateBehavior
  } = useBehaviorsList(props.entry!, props.updateEntry);
  const retentionTypeOptions: Array<SectionItem<MetricType>> = [{
    value: MetricType.Retention,
    label: t("retention.retention"),
    tooltip: {
      size: DisplaySize.Small,
      text: "".concat(t("retention.retention-tooltip"), "\n[").concat(t("common.learn-more"), "](https://docs.mixpanel.com/docs/analysis/reports/retention#overview)")
    }
  }, {
    value: MetricType.RetentionFrequency,
    label: t("retention.frequency"),
    tooltip: {
      size: DisplaySize.Small,
      text: "".concat(t("retention.frequency-tooltip"), "\n[").concat(t("common.learn-more"), "](https://docs.mixpanel.com/docs/analysis/reports/retention#frequency-view)")
    }
  }];
  const handleSettingsChanged = (update: RetentionQuerySettingsUpdate) => {
    const updatedEntry = produce(props.entry, draft => {
      draft.behavior.retentionUnboundedMode = update.unboundedMode;
      draft.measurement.retentionCumulative = Boolean(update.retentionCumulative);
    });
    props.updateEntry(updatedEntry);
  };
  const handleMetricTypeChanged = (metricType: MetricType | undefined) => {
    if (!metricType) {
      return;
    }
    const updatedEntry = produce(props.entry, draft => {
      draft.behavior.type = metricType;
      if (metricType === MetricType.RetentionFrequency) {
        draft.behavior.retentionType = "addiction";
        if (draft.behavior.behaviors && draft.behavior.behaviors.length > 1) {
          draft.behavior.behaviors = [draft.behavior.behaviors[0]];
        }
      } else {
        draft.behavior.retentionType = "birth";
        if (draft.behavior.behaviors && draft.behavior.behaviors.length < 2) {
          var _draft$behavior$behav, _draft$behavior$behav2;
          (_draft$behavior$behav = draft.behavior.behaviors) === null || _draft$behavior$behav === void 0 || _draft$behavior$behav.push(_objectSpread({}, (_draft$behavior$behav2 = draft.behavior.behaviors[0]) !== null && _draft$behavior$behav2 !== void 0 ? _draft$behavior$behav2 : {
            type: MetricType.Event,
            name: null,
            filters: []
          }));
        }
      }
    });
    props.updateEntry(updatedEntry);
  };
  return <div className={styles.retentionBehavior} data-sentry-component="RetentionBehaviorDefinition" data-sentry-source-file="behavior-definition.tsx">
      {behaviors.map((subBehavior, index) => {
      return <DraggableBehavior {...props} index={index} isLast={index === behaviors.length - 1} metricIndex={props.index} parentMetricIndex={props.parentMetricIndex} behavior={subBehavior} label={t("query-builder.select-event")}
      /** prevents behavioral or computed filters from showing up */ otherFilterOptions={[]} suffixLabel={props.entry.behavior.type === MetricType.Retention && index === 0 ? " ".concat(t("filters.group-operator.then")) : null} onSelect={behavior => handleUpdateBehavior(index, behavior)} />;
    })}
      <div className={styles.bottomControls}>
        {/* Retention vs Frequency select */}
        <SelectReact alwaysShowCaret menuWidth={-1} onChange={ev => handleMetricTypeChanged((ev as ChangeEvent<MetricType>).detail.item.value)} sections={[{
        items: retentionTypeOptions
      }]} selectedOption={props.entry.behavior.type === MetricType.Retention ? t("retention.retention") : t("retention.frequency")} size={ButtonSize.Small} theme={SelectTheme.Tertiary} data-sentry-element="SelectReact" data-sentry-source-file="behavior-definition.tsx" />

        {props.entry.behavior.type === MetricType.Retention && <RetentionCriteria {...props} />}

        {props.entry.behavior.type === MetricType.RetentionFrequency && <RetentionQuerySettingsReact countingMethod={getRetentionCountingMethod(props.entry)} onChange={handleSettingsChanged} retentionType={props.entry.behavior.retentionType} />}
      </div>
    </div>;
}